import { useEffect } from "react";
import { useDataSource } from "@firecms/core";
import { useSpaces } from "../hooks/useSpaces";
import { resolveLocalizedString } from "../localization";
import { accountsCollection } from "../collections/accounts";
import { eventsCollection } from "../collections/events";
import { eventEntityTitle } from "../actions/ManageEventButton";
import { spaceIdFromEventEntity } from "./eventIdentity";
import type { useAdminWorkspace } from "./useAdminWorkspace";

type WorkspaceTitleSyncProps = Pick<
  ReturnType<typeof useAdminWorkspace>,
  | "mode"
  | "selectedAccountId"
  | "selectedSpaceId"
  | "selectedEventId"
  | "setAccountTitle"
  | "setTitle"
  | "setSpaceTitle"
  | "setHydratedSpaceId"
>;

export function WorkspaceTitleSync({
  mode,
  selectedAccountId,
  selectedSpaceId,
  selectedEventId,
  setAccountTitle,
  setTitle,
  setSpaceTitle,
  setHydratedSpaceId,
}: WorkspaceTitleSyncProps) {
  const { fetchEntity } = useDataSource();
  const { spaces } = useSpaces(
    mode.kind === "space" && selectedAccountId
      ? { accountId: selectedAccountId }
      : undefined
  );

  useEffect(() => {
    if (!selectedAccountId) {
      return;
    }
    if (mode.kind !== "account" && mode.kind !== "space" && mode.kind !== "event") {
      return;
    }

    let cancelled = false;

    void fetchEntity({
      path: accountsCollection.path,
      collection: accountsCollection,
      entityId: selectedAccountId,
    }).then((account) => {
      if (cancelled || !account?.values.name) {
        return;
      }
      setAccountTitle(account.values.name);
      if (mode.kind === "account") {
        setTitle(account.values.name);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchEntity, mode.kind, selectedAccountId, setAccountTitle, setTitle]);

  useEffect(() => {
    if (mode.kind === "account") {
      setSpaceTitle("");
    }
  }, [mode.kind, selectedAccountId, setSpaceTitle]);

  useEffect(() => {
    if (mode.kind !== "space" || !selectedSpaceId || !spaces) {
      return;
    }

    const space = spaces.find((entry) => entry.id === selectedSpaceId);
    if (!space) {
      return;
    }

    setSpaceTitle(
      resolveLocalizedString(space.values.title) || space.values.name
    );
  }, [mode.kind, selectedSpaceId, setSpaceTitle, spaces]);

  useEffect(() => {
    if (mode.kind !== "event" || !selectedAccountId || !selectedEventId) {
      return;
    }

    let cancelled = false;

    void fetchEntity({
      path: `accounts/${selectedAccountId}/events`,
      collection: eventsCollection,
      entityId: selectedEventId,
    }).then((event) => {
      if (cancelled || !event) {
        return;
      }
      setTitle(eventEntityTitle(event));

      if (mode.kind === "event" && !mode.id.spaceId) {
        const spaceId = spaceIdFromEventEntity(event);
        if (spaceId) {
          setHydratedSpaceId(spaceId);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    fetchEntity,
    mode,
    selectedAccountId,
    selectedEventId,
    setHydratedSpaceId,
    setTitle,
  ]);

  useEffect(() => {
    if (mode.kind !== "event") {
      setHydratedSpaceId("");
    }
  }, [mode.kind, setHydratedSpaceId]);

  return null;
}
